// frontend/src/hooks/useSitePriorities.ts — the site-priorities read surface
// (list), mirroring useSiteTypes. Project-scoped server state (FRONTEND-PATTERNS
// §State): the key is `['site-priorities', projectId]` so a mutation's invalidate
// refreshes the list. The list row carries every field (no detail endpoint — the
// edit drawer pre-fills from the row), so there is no detail hook. The read is
// member-level (a vocabulary is named by every member; defining it stays
// `reference_data:manage`).
//
// 401 is the centralized apiClient's job (ADR-003) — errors bubble so the page's
// 5-state contract renders them.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SitePriorityListItem, SitePrioritiesListResponse } from '@ems-app/schemas/site-priorities/responses';
import { apiClient } from '../lib/apiClient.js';
import type { SmallSource } from './useOptions.js';

export const sitePrioritiesKeys = {
  /** List key — also the invalidation PREFIX. */
  all: (projectId: string) => ['site-priorities', projectId] as const,
};

/** The list read — the page's, and a form's Load the latest (a priority has no detail read: the list row is its read). */
export async function fetchSitePriorities(projectId: string): Promise<SitePrioritiesListResponse> {
  const r = await apiClient.get<SitePrioritiesListResponse>(`/projects/${projectId}/site-priorities`);
  return r.data;
}

// Module-level selector (referentially stable, §State select doctrine).
const selectItems = (r: SitePrioritiesListResponse): SitePriorityListItem[] => r.items;

export function useSitePriorities(projectId: string): UseQueryResult<SitePriorityListItem[], Error> {
  return useQuery({
    queryKey: sitePrioritiesKeys.all(projectId),
    queryFn: () => fetchSitePriorities(projectId),
    enabled: projectId.length > 0,
    select: selectItems,
  });
}

/** The priorities as a picker's source (ADR-080 §3 — the small kind, on the list's own key): the WHOLE list on the
 *  press in its curated order; a disabled priority is an inactive row. */
export function sitePrioritiesSource(projectId: string): SmallSource<SitePrioritiesListResponse> {
  return {
    kind: 'small',
    queryKey: sitePrioritiesKeys.all(projectId),
    fetch: () => fetchSitePriorities(projectId),
    rows: (r) => r.items.map((p) => ({ id: String(p.id), label: p.name, pickable: true, inactive: p.is_disabled, group: null })),
  };
}
